'use client';

import React from 'react';
import { BiTime, BiTrendingUp } from 'react-icons/bi';
import { HiArrowRight } from 'react-icons/hi';
import { useTranslation } from '@/hooks/useTranslation';
import { HiOutlineTrophy } from 'react-icons/hi2';
import { FaRegCircleCheck } from 'react-icons/fa6';
import LouiseButton from '@/components/buttons/LouiseButton';

type Props = { locale: string };

const TransformYourStrategy = ({ locale }: Props) => {
  const { t } = useTranslation(locale);

  const items = [
    {
      iconicText: t('Home/TransformYourStrategy/key1'),
      title: t('Home/TransformYourStrategy/key1-2'),
      description: t('Home/TransformYourStrategy/key1-3'),
      icon: HiOutlineTrophy,
      color: 'tw-from-green-500 tw-to-emerald-600',
      lightColor: 'tw-from-green-50 tw-to-emerald-50',
      textColor: 'tw-text-green-600',
      delay: '0',
    },
    {
      iconicText: t('Home/TransformYourStrategy/key2'),
      title: t('Home/TransformYourStrategy/key2-2'),
      description: t('Home/TransformYourStrategy/key2-3'),
      icon: BiTime,
      color: 'tw-from-blue-500 tw-to-cyan-600',
      lightColor: 'tw-from-blue-50 tw-to-cyan-50',
      textColor: 'tw-text-blue-600',
      delay: '150',
    },
    {
      iconicText: t('Home/TransformYourStrategy/key3'),
      title: t('Home/TransformYourStrategy/key3-2'),
      description: t('Home/TransformYourStrategy/key3-3'),
      icon: BiTrendingUp,
      color: 'tw-from-purple-500 tw-to-pink-600',
      lightColor: 'tw-from-purple-50 tw-to-pink-50',
      textColor: 'tw-text-purple-600',
      delay: '300',
    },
  ];

  return (
    <section className="tw-pt-16 lg:tw-pt-20 tw-pb-5 tw-relative tw-overflow-hidden  tw-bg-transparent>">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl tw-relative">
        {/* Header */}
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-gradient-to-r tw-from-blue-100 tw-to-purple-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <FaRegCircleCheck className="tw-w-4 tw-h-4" />
            {t('Home/TransformYourStrategy/badge')}
          </div>
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend']">
            {t('Home/TransformYourStrategy/title')}
          </h2>
          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-font-['Lexend']">
            {t('Home/TransformYourStrategy/subtitle')}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="tw-grid grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
          {items.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className="tw-group tw-relative tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100 tw-p-8 tw-transition-all tw-duration-500 hover:tw-shadow-2xl hover:tw-scale-105 tw-overflow-hidden"
                style={{
                  animationDelay: `${item.delay}ms`,
                  animation: 'fadeInUp 0.6s ease-out forwards',
                }}
              >
                {/* Background gradient overlay */}
                <div
                  className={`tw-absolute tw-inset-0 tw-bg-gradient-to-br ${item.lightColor} tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300`}
                ></div>

                {/* Content */}
                <div className="tw-relative tw-z-10">
                  {/* Icon and Stat */}
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                    <div
                      className={`tw-w-16 tw-h-16 tw-rounded-2xl tw-bg-gradient-to-br ${item.color} tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-transition-transform tw-duration-300 group-hover:tw-scale-110`}
                    >
                      <Icon className="tw-w-8 tw-h-8 tw-text-white" />
                    </div>
                    <div className="tw-text-right">
                      <div
                        className={`tw-text-3xl sm:tw-text-4xl tw-font-bold tw-bg-gradient-to-r ${item.color} tw-bg-clip-text tw-text-transparent tw-font-['Lexend']`}
                      >
                        {item.iconicText}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend'] tw-leading-tight">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="tw-text-gray-600 tw-leading-relaxed tw-font-['Lexend'] tw-mb-6 tw-text-sm">
                    {item.description}
                  </p>

                  {/* Hover indicator */}
                  <div className="tw-flex tw-items-center tw-gap-2 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-all tw-duration-300 tw-transform tw-translate-y-2 group-hover:tw-translate-y-0">
                    <span className={`tw-text-sm tw-font-medium ${item.textColor} tw-font-['Lexend']`}>
                      {t('Home/TransformYourStrategy/learn-more')}
                    </span>
                    <HiArrowRight
                      className={`tw-w-4 tw-h-4 ${item.textColor} tw-transition-transform tw-duration-300 group-hover:tw-translate-x-1`}
                    />
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="tw-absolute tw-top-0 tw-right-0 tw-w-20 tw-h-20 tw-bg-gradient-to-bl tw-from-white/10 tw-to-transparent tw-rounded-bl-full"></div>
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-16 tw-h-16 tw-bg-gradient-to-tr tw-from-white/5 tw-to-transparent tw-rounded-tr-full"></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="tw-text-center tw-mt-8">
          <LouiseButton
            color="blue"
            cta={t('Home/TransformYourStrategy/cta-text')}
            ctaTarget="register"
            locale={locale}
            gradiant={true}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default TransformYourStrategy;
